import { useEffect, useRef } from 'react';
import { SocketConnection } from '../socket/socket-main-thread/socket-connection';
import socketManager from '../socket/socket-main-thread/socket-manager';
import { MergeAudienceListsResponse } from '@/types/registration.types';
import { useAppDispatch } from '@/store/hooks';
import { fetchPasscodeLists } from '@/store/slices/passcode-lists';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';

export default function useMergeListsComplete(channel: number | null | undefined) {
  const dispatch = useAppDispatch();
  const projectUuidParam = useProjectUuidParam();
  const socket = useRef<SocketConnection | null>(null);

  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const projectUuidParamRef = useRef(projectUuidParam);
  projectUuidParamRef.current = projectUuidParam;
  const channelRef = useRef(channel);
  channelRef.current = channel;

  useEffect(() => {
    if (!channel) return;

    const namespace = `admin-${channel}`;
    socket.current = socketManager.getFromHook(namespace);

    const handleComplete = (response: MergeAudienceListsResponse) => {
      if (response.data.eventUuid !== projectUuidParamRef.current) return;

      if (channelRef.current) {
        dispatchRef.current(fetchPasscodeLists(channelRef.current));
      }

      toastTrigger('Audience list merge has finished', { type: EToastTypes.success, autoClose: 5000 });
    };

    socket.current.addListener<MergeAudienceListsResponse>('merge-lists-complete', handleComplete);

    return () => {
      socket.current?.removeListener('merge-lists-complete', handleComplete);
      socketManager.leave(namespace);
    };
  }, [channel]);
}
